/**
 * What this browser can actually turn into pixels.
 *
 * Every capture path (shutter, file picker, drag and drop) ends in
 * `createImageBitmap`, because the quality gate, the sha256 fingerprint and
 * the barcode decode all read raw pixels.
 *
 * HEIC is the case that matters, as the iPhone camera default. Chrome, Edge
 * and Firefox decode none of it. Safari decodes all of it, on macOS and on
 * iOS, through the system codecs. So "this browser cannot read HEIC" holds on
 * three engines and fails on the fourth, and the only honest test is to hand
 * the file to `createImageBitmap` and see what comes back. This module used
 * to refuse on the extension alone, which turned Safari's working decode into
 * a refusal the operator could do nothing about.
 *
 * The field path on an iPhone should never produce a HEIC at all. iOS Safari
 * transcodes a chosen photo to JPEG for a file input, and hands over the
 * untouched original only when that input's `accept` advertises HEIC.
 * `CaptureViewfinder` listed `.heic,.heif` there until 2026-09-17, which put
 * HEIC on the one path this file already described as safe.
 *
 * Decoding HEIC where the browser will not needs a WebAssembly build of
 * libheif, blocked today by the capture client's own Content-Security-Policy
 * (`script-src 'self'` with no `wasm-unsafe-eval`). That decision is open.
 */

/** Extensions of the HEIF family, lower case, with the dot. */
const HEIF_EXTENSIONS = ['.heic', '.heif'] as const;

/** MIME types for the same formats, for a platform that does set one. */
const HEIF_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

/** Whether a file is HEIC or HEIF, by extension or by declared type. */
export function isHeifFamily(file: File): boolean {
  const name = file.name.toLowerCase();
  if (HEIF_EXTENSIONS.some((extension) => name.endsWith(extension))) return true;
  return HEIF_TYPES.includes(file.type.toLowerCase());
}

/**
 * Whether a chosen file is worth handing to `createImageBitmap`.
 *
 * Some platforms report an empty `type` for a file the picker offered, so an
 * empty type is accepted on the strength of its extension rather than
 * rejected as "not an image".
 */
export function looksLikeImage(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  if (file.type !== '') return false;
  return /\.(jpe?g|png|webp|gif|avif|bmp|heic|heif)$/i.test(file.name);
}

/**
 * What to tell the operator about a file this browser turned out not to
 * decode. Names the format when it is one a reader would recognise, so the
 * instruction is actionable rather than a bare "could not be read".
 */
export function decodeFailureMessage(file: File): string {
  if (isHeifFamily(file)) {
    const isHeif =
      file.name.toLowerCase().endsWith('.heif') || file.type.toLowerCase().startsWith('image/heif');
    const label = isHeif ? 'HEIF' : 'HEIC';
    return `${label} images cannot be read by this browser. Export the photo as JPEG and choose it again.`;
  }
  return 'That image could not be read. Try another photo, or export it as JPEG.';
}
